import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { LoaderCircle, Mail, Phone, Plus, Trash2, User } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { InlineMessage, InlineStatusBadge } from "../../../components/konto-helpers";
import { usePlatformAuth } from "../../../lib/use-platform-auth";

type OrganizationUser = {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  phoneNumber: string | null;
  role: string;
};

export function BrukereTab() {
  const { organizationSlug } = useParams<{ organizationSlug: string }>();
  const { organizations, sessionData } = usePlatformAuth();

  const currentOrg = organizations.find((o) => o.slug === organizationSlug);
  const canManageRoles = currentOrg && ["admin", "owner", "super_admin"].includes(currentOrg.role);
  const currentUserId = sessionData?.user?.id;

  const [members, setMembers] = useState<OrganizationUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [changingRoleId, setChangingRoleId] = useState<string | null>(null);
  const [inviteInput, setInviteInput] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [inviting, setInviting] = useState(false);

  useEffect(() => {
    if (!organizationSlug) return;
    void fetchMembers(organizationSlug);
  }, [organizationSlug]);

  const fetchMembers = async (slug: string) => {
    setLoading(true);
    setError(null);
    try {
      const api = import.meta.env.VITE_API_URL || "http://localhost:9000";
      const response = await fetch(`${api}/api/organizations/${slug}/users`, { credentials: "include" });
      if (!response.ok) throw new Error();
      setMembers((await response.json()) as OrganizationUser[]);
    } catch {
      setError("Vi klarte ikke å hente brukerne i organisasjonen.");
      setMembers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = inviteInput.trim();
    if (!value) return;

    setInviting(true);
    setActionError(null);
    setActionMessage(null);

    const isEmail = value.includes("@");
    let normalized = value;
    if (!isEmail) {
      let phone = value.replace(/\s+/g, "");
      if (!phone.startsWith("+") && !phone.startsWith("00")) phone = `+47${phone}`;
      else if (phone.startsWith("00")) phone = `+${phone.slice(2)}`;
      normalized = phone;
    }

    try {
      const api = import.meta.env.VITE_API_URL || "http://localhost:9000";
      const response = await fetch(`${api}/api/organizations/${organizationSlug}/users/invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email: isEmail ? normalized : undefined,
          phoneNumber: !isEmail ? normalized : undefined,
          role: inviteRole,
        }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error((data as any).message || "Kunne ikke invitere bruker.");
      }
      setInviteInput("");
      setActionMessage("Bruker er invitert.");
      await fetchMembers(organizationSlug!);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Kunne ikke invitere bruker.");
    } finally {
      setInviting(false);
    }
  };

  const handleRemove = async (userId: string) => {
    if (!window.confirm("Er du sikker på at du vil fjerne dette medlemmet?")) return;
    setDeletingId(userId);
    setActionError(null);
    setActionMessage(null);
    try {
      const api = import.meta.env.VITE_API_URL || "http://localhost:9000";
      const response = await fetch(`${api}/api/organizations/${organizationSlug}/users/${userId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error((data as any).error || "Kunne ikke fjerne medlem.");
      }
      setActionMessage("Medlem er fjernet.");
      await fetchMembers(organizationSlug!);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Kunne ikke fjerne medlem.");
    } finally {
      setDeletingId(null);
    }
  };

  const handleRoleChange = async (userId: string, newRole: string) => {
    setChangingRoleId(userId);
    setActionError(null);
    setActionMessage(null);
    try {
      const api = import.meta.env.VITE_API_URL || "http://localhost:9000";
      const response = await fetch(`${api}/api/organizations/${organizationSlug}/users/${userId}/role`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ role: newRole }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error((data as any).error || "Kunne ikke endre rollen.");
      }
      setActionMessage("Rollen er oppdatert.");
      await fetchMembers(organizationSlug!);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Kunne ikke endre rollen.");
    } finally {
      setChangingRoleId(null);
    }
  };

  return (
    <section
      className="rounded-3xl bg-white/50 p-8 backdrop-blur-sm animate-in fade-in slide-in-from-bottom-4 duration-500"
      style={{ border: "1px dashed var(--border)" }}
    >
      <div className="mb-10 flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-3">
          <h2
            className="text-2xl font-black text-[#1a2e35]"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Brukere
          </h2>
          <p className="text-base font-medium text-muted-foreground max-w-md leading-relaxed">
            Se hvem som har tilgang til organisasjonen og hvilke roller de har.
          </p>
        </div>

        <form onSubmit={handleInvite} className="flex w-full flex-col gap-4 sm:w-auto">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
            <div className="space-y-2 sm:min-w-[320px]">
              <Label htmlFor="inviteInput" className="text-[10px] font-black uppercase tracking-wider text-muted-foreground/60">
                E-post eller mobilnummer
              </Label>
              <div className="relative">
                {inviteInput.includes("@") ? (
                  <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/40" />
                ) : (
                  <Phone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/40" />
                )}
                <Input
                  id="inviteInput"
                  placeholder="navn@firma.no eller 900 00 000"
                  value={inviteInput}
                  onChange={(e) => setInviteInput(e.target.value)}
                  className="h-12 rounded-xl pl-10"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={inviting || !inviteInput.trim()}
              className="h-12 rounded-xl px-10 font-bold shadow-lg shadow-accent/20 transition-all hover:scale-105 active:scale-95"
            >
              {inviting ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
              Inviter
            </Button>
          </div>
        </form>
      </div>

      {actionError ? <InlineMessage tone="error">{actionError}</InlineMessage> : null}
      {actionMessage ? <InlineMessage tone="success">{actionMessage}</InlineMessage> : null}
      {error ? <InlineMessage tone="error">{error}</InlineMessage> : null}

      <div className="mt-6">
        {loading ? (
          <div
            className="flex h-32 items-center justify-center rounded-3xl"
            style={{ border: "1px dashed var(--border)", background: "var(--muted)/10" }}
          >
            <LoaderCircle className="h-5 w-5 animate-spin text-primary" />
          </div>
        ) : members.length === 0 ? (
          <div className="flex h-32 items-center justify-center rounded-xl" style={{ background: "var(--muted)" }}>
            <p className="text-sm" style={{ color: "var(--muted-foreground)" }}>
              Ingen brukere funnet for denne organisasjonen.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {members.map((member) => {
              const fullName = [member.firstName, member.lastName].filter(Boolean).join(" ");
              return (
                <div
                  key={member.id}
                  className="flex flex-col gap-4 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-border transition-all hover:shadow-md sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="flex min-w-0 items-center gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-accent/10 text-accent">
                      <User className="h-6 w-6" />
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-foreground">
                        {fullName || member.email || member.phoneNumber || "Uten navn"}
                      </p>
                      <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                        {member.email && (
                          <div className="flex items-center gap-1.5">
                            <Mail className="h-3.5 w-3.5 opacity-60" />
                            <span>{member.email}</span>
                          </div>
                        )}
                        {member.phoneNumber && (
                          <div className="flex items-center gap-1.5">
                            <Phone className="h-3.5 w-3.5 opacity-60" />
                            <span>{member.phoneNumber}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {canManageRoles && member.role !== "owner" && member.id !== currentUserId ? (
                      <div className="relative">
                        {changingRoleId === member.id ? (
                          <div className="flex h-8 w-24 items-center justify-center">
                            <LoaderCircle className="h-4 w-4 animate-spin text-muted-foreground" />
                          </div>
                        ) : (
                          <select
                            value={member.role}
                            onChange={(e) => void handleRoleChange(member.id, e.target.value)}
                            className="h-8 rounded-lg border border-border bg-white px-2 text-xs font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30"
                          >
                            <option value="member">member</option>
                            <option value="admin">admin</option>
                          </select>
                        )}
                      </div>
                    ) : (
                      <InlineStatusBadge ok>{member.role}</InlineStatusBadge>
                    )}
                    {member.role !== "owner" && member.id !== currentUserId && (
                      <button
                        type="button"
                        onClick={() => handleRemove(member.id)}
                        disabled={deletingId === member.id}
                        className="flex h-10 w-10 items-center justify-center rounded-xl border border-destructive/20 bg-destructive/5 text-destructive transition-all hover:bg-destructive/10 disabled:opacity-50"
                      >
                        {deletingId === member.id ? (
                          <LoaderCircle className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
